import React from 'react';
import { Alert, Card, Checkbox, Button, message } from 'antd';
import { CheckCircleOutlined, EditOutlined } from '@ant-design/icons';
import { useCustomOfferForm } from './hooks/useCustomOfferForm';
import {
	BasicInformationForm,
	PricingForm,
	CustomOffersTable,
	ClientFeaturesForm,
	ResellerFeaturesForm,
	SuccessModal,
} from './components';
import UserRegistrationPage from '@/pages/UserRegistration/UserRegistrationPage';
import Step2 from '@/pages/ResellerQualificationForm/Step2';

const CustomOffer: React.FC = () => {
	const {
		formData,
		isLoading,
		offers,
		generatedLink,
		accountDetails,
		showTrialPeriod,
		showFeatures,
		showUserData,
		showModal,
		errors,
		isClient,
		customOfferRegistrationDataSubmitted,
		clientFeatures,
		resellerFeatures,
		updateFormData,
		updateUserData,
		updateFeatures,
		updateVoiceProviderPricing,
		getFeatureValue,
		updateFeatureValue,
		setShowTrialPeriod,
		setShowFeatures,
		setShowUserData,
		setShowModal,
		setCustomOfferRegistrationDataSubmitted,
		handleSubmit,
		getFieldError,
	} = useCustomOfferForm();

	const copyToClipboard = (text: string): void => {
		navigator.clipboard.writeText(text);
		message.success('Copied to clipboard!');
	};

	const handleShowFeaturesChange = (checked: boolean): void => {
		setShowFeatures(checked);
	};

	const handleEditRegistrationData = (): void => {
		setCustomOfferRegistrationDataSubmitted(false);
		updateFormData({
			userData: {
				username: '',
				emailAddress: '',
				password: '',
				phoneNumber: '',
				type: formData.forUserType,
				businessName: '',
				businessType: '',
				otherBusinessType: '',
			},
		});
	};

	const handleCloseModal = (): void => {
		setShowModal(false);
	};

	const renderErrorAlert = (): JSX.Element | null => {
		if (!errors.general) {
			return null;
		}

		return <Alert message={errors.general} type="error" showIcon className="mb-6" />;
	};

	const renderRegistrationSuccessAlert = (): JSX.Element => {
		return (
			<Alert
				message={
					<div className="flex items-center justify-between">
						<div className="flex items-center">
							<CheckCircleOutlined className="mr-2" />
							<span className="font-medium">Registration data submitted successfully!</span>
						</div>
						<Button
							type="link"
							icon={<EditOutlined />}
							onClick={handleEditRegistrationData}
							className="text-green-600 hover:text-green-800 p-0 h-auto"
						>
							Edit Registration Data
						</Button>
					</div>
				}
				description="The user registration data has been collected and is ready for offer generation."
				type="success"
				showIcon={false}
				className="mb-6"
			/>
		);
	};

	const renderUserDataSection = (): JSX.Element | null => {
		if (!showUserData) {
			return null;
		}

		return (
			<div className="border-t pt-6">
				<h3 className="text-lg font-medium text-gray-900 mb-4">Account Details</h3>

				{customOfferRegistrationDataSubmitted ? (
					renderRegistrationSuccessAlert()
				) : (
					<>
						{isClient && (
							<UserRegistrationPage
								agentType="Inbound"
								fromCustomOfferAccountGeneration={true}
								retrieveSubmissionData={updateUserData}
							/>
						)}

						{!isClient && (
							<Step2
								customOffer={false}
								encodedData={null}
								fromCustomOfferAccountGeneration={true}
								retrieveSubmissionData={updateUserData}
							/>
						)}
					</>
				)}
			</div>
		);
	};

	const renderFeaturesSection = (): JSX.Element => {
		return (
			<div className="border-t pt-6">
				<div className="flex items-center mb-4">
					<Checkbox
						id="showFeatures"
						checked={showFeatures}
						onChange={(e) => handleShowFeaturesChange(e.target.checked)}
						disabled={formData.forUserType === 'Client'}
					>
						<span className="text-lg font-medium">
							{formData.forUserType === 'Client'
								? 'Features (Required for Clients)'
								: 'Include Features'}
						</span>
					</Checkbox>
				</div>
				{formData.forUserType === 'Client' && !showFeatures && (
					<Alert
						message="Features are required for Client offers"
						type="error"
						showIcon
						className="mt-1"
					/>
				)}

				{showFeatures && formData.forUserType === 'Client' && (
					<ClientFeaturesForm
						formData={formData}
						clientFeatures={clientFeatures}
						updateFeatures={updateFeatures}
						updateVoiceProviderPricing={updateVoiceProviderPricing}
						getFeatureValue={getFeatureValue}
						updateFeatureValue={updateFeatureValue}
						setFormData={updateFormData}
					/>
				)}

				{showFeatures && !isClient && (
					<ResellerFeaturesForm
						resellerFeatures={resellerFeatures}
						updateFormData={updateFormData}
					/>
				)}
			</div>
		);
	};

	return (
		<div className="max-w-6xl mx-auto p-6">
			<h1 className="text-3xl font-bold mb-8">Custom Offer Management</h1>

			{renderErrorAlert()}

			<Card title="Generate New Custom Offer" className="mb-8">
				<form onSubmit={handleSubmit} className="space-y-6">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<BasicInformationForm
							formData={formData}
							updateFormData={updateFormData}
							getFieldError={getFieldError}
							isClient={isClient}
						/>

						<PricingForm
							formData={formData}
							updateFormData={updateFormData}
							getFieldError={getFieldError}
							showTrialPeriod={showTrialPeriod}
							setShowTrialPeriod={setShowTrialPeriod}
							setShowUserData={setShowUserData}
						/>
					</div>

					{renderUserDataSection()}
					{renderFeaturesSection()}

					<div className="flex justify-end">
						<Button
							type="primary"
							htmlType="submit"
							loading={isLoading}
							disabled={isLoading}
							className="px-6 py-2"
						>
							{isLoading ? 'Generating...' : 'Generate Offer'}
						</Button>
					</div>
				</form>
			</Card>

			<Card title="Existing Custom Offers">
				<CustomOffersTable offers={offers} isLoading={isLoading} />
			</Card>

			<SuccessModal
				showModal={showModal}
				generatedLink={generatedLink}
				accountDetails={accountDetails}
				onClose={handleCloseModal}
				onCopyLink={copyToClipboard}
			/>
		</div>
	);
};

export default CustomOffer;
